/**
 * PlanRouteMap — minimal Leaflet map for the Plan cockpit.
 *
 * No chrome, no header, no side panels — just pins on a square canvas.
 * The PlanCockpit owns the surrounding header, day filters, and the
 * nearest-installs rail, so this component is intentionally bare.
 *
 * Pins are color-coded by install readiness; clicking a pin calls
 * onSelectJob so the parent can sync the proximity anchor.
 */
import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Job, Issue } from "@/data/types";
import { getInstallReadiness, type InstallReadiness } from "@/components/JobWorkflowStatus";
import { useAppData } from "@/data/AppDataContext";

const CITY_COORDS: Record<string, [number, number]> = {
  Tampa: [27.9506, -82.4572],
  "St. Petersburg": [27.7676, -82.6403],
  Clearwater: [27.9659, -82.8001],
  Sarasota: [27.3364, -82.5307],
  Bradenton: [27.4989, -82.5748],
  "Fort Myers": [26.6406, -81.8723],
  "Cape Coral": [26.5629, -81.9495],
  Naples: [26.1420, -81.7948],
  Lakeland: [28.0395, -81.9498],
  Brandon: [27.9378, -82.2860],
  Riverview: [27.8661, -82.3265],
  Largo: [27.9095, -82.7873],
  Parrish: [27.5872, -82.4262],
  "Punta Gorda": [26.9298, -82.0454],
};

function getCoords(job: Job): [number, number] {
  return CITY_COORDS[job.address.city] ?? [27.95, -82.46];
}

const READY_COLOR: Record<InstallReadiness, string> = {
  "Ready for Install": "hsl(142 71% 45%)",
  "Partially Ready": "hsl(38 92% 50%)",
  "Not Ready": "hsl(0 84% 60%)",
};

function makePinIcon(color: string, isSelected: boolean): L.DivIcon {
  const size = isSelected ? 22 : 16;
  return L.divIcon({
    className: "plan-route-pin",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};
      border:2px solid hsl(var(--background));
      box-shadow:0 1px 4px hsl(var(--foreground) / 0.25);
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

interface Props {
  jobs: Job[];
  issues: Issue[];
  selectedJobId: string | null;
  onSelectJob: (jobId: string) => void;
}

export function PlanRouteMap({ jobs, issues, selectedJobId, onSelectJob }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const aliveRef = useRef(true);
  const { buildReviewEvents } = useAppData();

  const pins = useMemo(
    () =>
      jobs.map((j) => {
        const open = issues.filter(
          (i) => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"),
        );
        return {
          job: j,
          coords: getCoords(j),
          readiness: getInstallReadiness(j, open, buildReviewEvents).status,
        };
      }),
    [jobs, issues, buildReviewEvents],
  );

  // Init map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView([27.5, -82.0], 8);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      maxZoom: 19,
    }).addTo(map);
    L.control
      .attribution({ position: "bottomright", prefix: false })
      .addAttribution("© OSM · CARTO")
      .addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapInstance.current = map;
    // Defer invalidate to ensure the container has its final size.
    const timeoutId = window.setTimeout(() => {
      if (!aliveRef.current || !mapInstance.current) return;
      mapInstance.current.invalidateSize();
    }, 0);
    return () => {
      aliveRef.current = false;
      window.clearTimeout(timeoutId);
      map.stop();
      map.remove();
      mapInstance.current = null;
    };
  }, []);

  // Resize observer keeps the map crisp during fullscreen toggles.
  useEffect(() => {
    if (!mapRef.current || !mapInstance.current) return;
    const ro = new ResizeObserver(() => {
      const map = mapInstance.current;
      if (!aliveRef.current || !map) return;
      requestAnimationFrame(() => {
        if (!aliveRef.current || !mapInstance.current) return;
        mapInstance.current.invalidateSize();
      });
    });
    ro.observe(mapRef.current);
    return () => ro.disconnect();
  }, []);

  // Render markers + fit bounds
  useEffect(() => {
    const map = mapInstance.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (pins.length === 0) return;

    const bounds = L.latLngBounds([]);
    pins.forEach(({ job, coords, readiness }) => {
      const marker = L.marker(coords, {
        icon: makePinIcon(READY_COLOR[readiness], job.id === selectedJobId),
      });
      marker.on("click", () => onSelectJob(job.id));
      marker.bindTooltip(`${job.customerName} · ${job.address.city}`, {
        direction: "top",
        offset: [0, -10],
      });
      marker.addTo(layer);
      bounds.extend(coords);
    });
    if (pins.length > 1) {
      map.fitBounds(bounds, { padding: [32, 32], maxZoom: 11, animate: false });
    } else {
      map.setView(pins[0].coords, 10, { animate: false });
    }
  }, [pins, selectedJobId, onSelectJob]);

  return <div ref={mapRef} className="h-full w-full bg-muted/20" />;
}
